<!-- src/components/stock/StockInternalTransferModal.vue -->

<template>
  <div
    ref="modalElement"
    class="modal core-materialize-modal core-materialize-modal--wide internal-transfer-modal"
  >
    <!-- =====================================================
         CONTENT
         ===================================================== -->
    <div class="modal-content">
      <!-- ===================================================
           HEADER
           =================================================== -->
      <div class="modal-heading-row">
        <div>
          <h5 class="modal-heading">Internal Transfer</h5>

          <p class="grey-text text-darken-1 modal-subtitle">
            Enter the quantity transferred in Order Units. The resulting Stock Out and
            Stock In quantities are calculated from the Stock Database conversion.
          </p>
        </div>

        <button
          class="core-icon-button modal-x-button"
          type="button"
          aria-label="Close"
          @click="close"
        >
          <i class="material-icons">close</i>
        </button>
      </div>

      <!-- ===================================================
           ERROR
           =================================================== -->
      <div v-if="error" class="core-callout core-callout--danger error-box">
        {{ error }}
      </div>

      <!-- ===================================================
           LOADING
           =================================================== -->
      <div v-if="loading" class="progress">
        <div class="indeterminate"></div>
      </div>

      <!-- ===================================================
           FORM
           =================================================== -->
      <form v-else id="stock-internal-transfer-form" @submit.prevent="saveTransfer">
        <!-- =================================================
             TRANSFER DETAILS
             ================================================= -->
        <div class="transfer-header-grid">
          <!-- TRANSFER DATE -->
          <div class="form-field">
            <label for="internal-transfer-date"> Transfer Date </label>

            <input
              id="internal-transfer-date"
              v-model="transferDate"
              type="date"
              class="form-input"
              required
            />
          </div>

          <!-- TRANSFER REFERENCE -->
          <div class="form-field">
            <label for="internal-transfer-reference"> Transfer Reference </label>

            <input
              id="internal-transfer-reference"
              :value="transferReference"
              type="text"
              class="form-input generated-reference"
              readonly
              tabindex="-1"
            />

            <span class="field-helper"> Generated automatically </span>
          </div>

          <!-- VENUE FROM -->
          <div class="form-field">
            <label for="internal-transfer-from"> Venue From </label>

            <select
              id="internal-transfer-from"
              v-model="fromVenueId"
              class="browser-default form-select"
              required
              @change="fromVenueChanged"
            >
              <option value="" disabled>Select Venue</option>

              <option v-for="venue in venues" :key="venue.id" :value="venue.id">
                {{ venue.label }}
              </option>
            </select>
          </div>

          <!-- VENUE TO -->
          <div class="form-field">
            <label for="internal-transfer-to"> Venue To </label>

            <select
              id="internal-transfer-to"
              v-model="toVenueId"
              class="browser-default form-select"
              required
            >
              <option value="" disabled>Select Venue</option>

              <option
                v-for="venue in destinationVenues"
                :key="venue.id"
                :value="venue.id"
              >
                {{ venue.label }}
              </option>
            </select>
          </div>
        </div>

        <!-- =================================================
             MOVEMENT SUMMARY
             ================================================= -->
        <div v-if="fromVenueId && toVenueId" class="movement-summary">
          <div class="movement-side movement-out">
            <i class="material-icons"> remove_circle_outline </i>

            <div>
              <strong>
                {{ venueLabel(fromVenueId) }}
              </strong>

              <span> Stock Out </span>
            </div>
          </div>

          <i class="material-icons movement-arrow"> arrow_forward </i>

          <div class="movement-side movement-in">
            <i class="material-icons"> add_circle_outline </i>

            <div>
              <strong>
                {{ venueLabel(toVenueId) }}
              </strong>

              <span> Stock In </span>
            </div>
          </div>
        </div>

        <!-- =================================================
             ADD PRODUCT
             ================================================= -->
        <div class="add-product-panel">
          <div class="add-product-grid">
            <!-- PRODUCT -->
            <div class="form-field">
              <label> Product Name </label>

              <select
                v-model="newLine.product_key"
                class="browser-default form-select"
                @change="productChanged"
              >
                <option value="" disabled>Select Product</option>

                <option
                  v-for="product in availableProductOptions"
                  :key="product.key"
                  :value="product.key"
                >
                  {{ product.product_name }}
                </option>
              </select>
            </div>

            <!-- SUPPLIER -->
            <div class="form-field">
              <label> Supplier </label>

              <select
                v-model="newLine.product_id"
                class="browser-default form-select"
                :disabled="!newLine.product_key"
                @change="supplierChanged"
              >
                <option value="" disabled>Select Supplier</option>

                <option
                  v-for="variant in supplierOptions"
                  :key="variant.id"
                  :value="variant.id"
                >
                  {{ variant.supplier }}
                </option>
              </select>
            </div>

            <!-- ORDER UNIT -->
            <div class="form-field">
              <label> Order Unit </label>

              <div
                class="unit-display"
                :class="{ 'unit-display--empty': !selectedVariant }"
              >
                {{ selectedVariant ? selectedOrderUnit : "—" }}
              </div>
            </div>

            <!-- QUANTITY -->
            <div class="form-field">
              <label> Qty Transferred </label>

              <input
                v-model.number="newLine.quantity"
                type="number"
                min="0"
                step="0.01"
                inputmode="decimal"
                class="form-input quantity-input"
                :disabled="!selectedVariant"
              />

              <span v-if="selectedVariant" class="field-helper">
                Quantity in {{ selectedOrderUnit }}
              </span>
            </div>

            <!-- ADD -->
            <div class="add-product-action">
              <button
                class="btn teal darken-2 waves-effect waves-light"
                type="button"
                :disabled="!canAddLine"
                @click="addLine"
              >
                <i class="material-icons left"> add </i>

                Add
              </button>
            </div>
          </div>

          <!-- ===============================================
               CONVERSION PREVIEW
               =============================================== -->
          <div v-if="selectedVariant" class="conversion-preview">
            <div class="conversion-definition">
              <span> Stock Database Conversion </span>

              <strong>
                1 {{ selectedOrderUnit }}
                =
                {{ formatNumber(selectedStockUnitsPerOrderUnit) }}
                {{ selectedStockUnit }}
              </strong>
            </div>

            <div v-if="number(newLine.quantity) > 0" class="conversion-result">
              <span> This transfer will move </span>

              <strong>
                {{ formatNumber(newLineStockUnits) }}
                {{ selectedStockUnit }}
              </strong>
            </div>
          </div>

          <!-- ===============================================
               MISSING CONVERSION
               =============================================== -->
          <div
            v-if="selectedVariant && selectedStockUnitsPerOrderUnit <= 0"
            class="conversion-warning"
          >
            <i class="material-icons"> warning_amber </i>

            <span>
              This product does not have a valid Stock Units per Order Unit conversion in
              the Stock Database.
            </span>
          </div>
        </div>

        <!-- =================================================
             TRANSFER LINES
             ================================================= -->
        <div v-if="lines.length" class="table-wrap transfer-lines-wrap">
          <table class="striped highlight core-table transfer-lines-table">
            <thead>
              <tr>
                <th>Product Name</th>

                <th>Supplier</th>

                <th>Order Unit</th>

                <th class="right-align">Qty Transferred</th>

                <th class="right-align">Stock Out / In</th>

                <th class="right-align">Transfer Value</th>

                <th class="center-align">Action</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="line in lines" :key="line.product_key">
                <!-- PRODUCT -->
                <td class="product-name">
                  {{ line.product_name }}
                </td>

                <!-- SUPPLIER -->
                <td>
                  {{ line.supplier || "—" }}
                </td>

                <!-- ORDER UNIT -->
                <td>
                  {{ line.order_unit || "—" }}
                </td>

                <!-- ORDER UNIT QTY -->
                <td class="right-align">
                  <strong>
                    {{ formatNumber(line.quantity_order_units) }}
                  </strong>

                  {{ line.order_unit }}
                </td>

                <!-- STOCK UNITS -->
                <td class="right-align stock-movement-cell">
                  <strong>
                    {{ formatNumber(line.quantity_transferred) }}
                  </strong>

                  {{ line.stock_unit }}
                </td>

                <!-- VALUE -->
                <td class="right-align line-total">
                  {{ money(lineTotal(line)) }}
                </td>

                <!-- REMOVE -->
                <td class="center-align">
                  <button
                    class="btn-flat red-text text-darken-2 remove-button"
                    type="button"
                    title="Remove"
                    @click="removeLine(line.product_key)"
                  >
                    <i class="material-icons"> delete </i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- ===============================================
               MOVEMENT EXPLANATION
               =============================================== -->
          <div class="transfer-explanation">
            <i class="material-icons"> swap_horiz </i>

            <span>
              The Stock Out quantity at
              <strong>{{ venueLabel(fromVenueId) }}</strong>
              and Stock In quantity at
              <strong>{{ venueLabel(toVenueId) }}</strong>
              will be identical Stock Unit quantities.
            </span>
          </div>

          <!-- ===============================================
               TOTAL
               =============================================== -->
          <div class="transfer-total">
            <span> Transfer Total </span>

            <strong>
              {{ money(transferTotal) }}
            </strong>
          </div>
        </div>

        <!-- =================================================
             NO LINES
             ================================================= -->
        <div v-else class="core-empty-state empty-lines">
          No products have been added.
        </div>
      </form>
    </div>

    <!-- =====================================================
         FOOTER
         ===================================================== -->
    <div class="modal-footer transfer-modal-footer">
      <button
        class="btn-flat waves-effect"
        type="button"
        :disabled="saving"
        @click="close"
      >
        Cancel
      </button>

      <button
        class="btn teal darken-2 waves-effect waves-light"
        type="submit"
        form="stock-internal-transfer-form"
        :disabled="saving || !transferValid"
      >
        <i class="material-icons left"> swap_horiz </i>

        {{ saving ? "Saving..." : "Save Transfer" }}
      </button>
    </div>
  </div>
</template>

<script>
import { auth } from "@/firebase";

import { useVenueContext } from "@/scripts/useScripts/useVenueContext";

import { listVenuesForSnapshotDropdown } from "@/scripts/sqlApiHelper/venuesApi";

import { saveStockInternalTransfer } from "@/scripts/sqlApiHelper/stockCountApi";

import { fetchStockUnits } from "@/scripts/sqlApiHelper/stockUnitsApi";

/* =========================================================
   PRODUCT KEY
   ========================================================= */

function productKey(group, name) {
  return [
    String(group ?? "")
      .trim()
      .toLowerCase(),

    String(name ?? "")
      .trim()
      .toLowerCase(),
  ].join("::");
}

/* =========================================================
   TODAY

   Returns YYYY-MM-DD using local time.
   ========================================================= */

function today() {
  const date = new Date();

  const adjusted = new Date(date.getTime() - date.getTimezoneOffset() * 60000);

  return adjusted.toISOString().slice(0, 10);
}

/* =========================================================
   EMPTY PRODUCT LINE
   ========================================================= */

function emptyLine() {
  return {
    product_key: "",
    product_id: "",
    quantity: 0,
  };
}

export default {
  name: "StockInternalTransferModal",

  emits: ["saved"],

  props: {
    activeVenueId: {
      type: String,
      default: "",
    },
  },

  /* =========================================================
     VENUE CONTEXT
     ========================================================= */

  setup() {
    const { isOperations, userType, allowedVenueIds } = useVenueContext();

    return {
      contextIsOperations: isOperations,
      contextUserType: userType,
      contextAllowedVenueIds: allowedVenueIds,
    };
  },

  data() {
    return {
      modalInstance: null,

      /* -----------------------------------------------------
         DATABASE DATA
         ----------------------------------------------------- */

      stockItems: [],
      venues: [],

      /* -----------------------------------------------------
         HEADER
         ----------------------------------------------------- */

      transferDate: today(),
      transferReference: "",

      fromVenueId: "",
      toVenueId: "",

      /* -----------------------------------------------------
         LINES
         ----------------------------------------------------- */

      lines: [],
      newLine: emptyLine(),

      /* -----------------------------------------------------
         STATE
         ----------------------------------------------------- */

      loading: false,
      saving: false,
      error: null,
    };
  },

  computed: {
    /* =====================================================
       DESTINATION VENUES
       ===================================================== */

    destinationVenues() {
      return this.venues.filter((venue) => venue.id !== this.fromVenueId);
    },

    /* =====================================================
       AVAILABLE PRODUCTS

       Collapse supplier variants into one Product Name
       option, exactly as before.
       ===================================================== */

    availableProductOptions() {
      const existing = new Set(this.lines.map((line) => line.product_key));

      const products = new Map();

      for (const product of this.stockItems) {
        const name = String(product.item_name ?? "").trim();

        const group = String(product.product_group ?? "").trim();

        if (!name || !group) {
          continue;
        }

        const key = productKey(group, name);

        if (existing.has(key) || products.has(key)) {
          continue;
        }

        products.set(key, {
          key,
          product_name: name,
          product_group: group,
        });
      }

      return Array.from(products.values()).sort((a, b) =>
        a.product_name.localeCompare(b.product_name, "en", {
          sensitivity: "base",
        })
      );
    },

    /* =====================================================
       SUPPLIER OPTIONS
       ===================================================== */

    supplierOptions() {
      if (!this.newLine.product_key) {
        return [];
      }

      return this.stockItems
        .filter(
          (product) =>
            productKey(product.product_group, product.item_name) ===
            this.newLine.product_key
        )
        .sort((a, b) =>
          String(a.supplier ?? "").localeCompare(String(b.supplier ?? ""), "en", {
            sensitivity: "base",
          })
        );
    },

    /* =====================================================
       SELECTED STOCK DATABASE VARIANT
       ===================================================== */

    selectedVariant() {
      return (
        this.stockItems.find((product) => product.id === this.newLine.product_id) || null
      );
    },

    /* =====================================================
       SELECTED ORDER UNIT
       ===================================================== */

    selectedOrderUnit() {
      return this.orderUnitFor(this.selectedVariant);
    },

    /* =====================================================
       SELECTED STOCK UNIT
       ===================================================== */

    selectedStockUnit() {
      return this.stockUnitFor(this.selectedVariant);
    },

    /* =====================================================
       STOCK UNITS PER ORDER UNIT

       Same conversion concept as Delivery Note.
       ===================================================== */

    selectedStockUnitsPerOrderUnit() {
      return this.stockUnitsPerOrderUnit(this.selectedVariant);
    },

    /* =====================================================
       NEW LINE STOCK QUANTITY

       Qty entered is ORDER UNITS.

       Example:
       2 cases × 24 bottles = 48 stock units.
       ===================================================== */

    newLineStockUnits() {
      return this.number(this.newLine.quantity) * this.selectedStockUnitsPerOrderUnit;
    },

    /* =====================================================
       CAN ADD
       ===================================================== */

    canAddLine() {
      return Boolean(
        this.selectedVariant &&
          this.number(this.newLine.quantity) > 0 &&
          this.selectedStockUnitsPerOrderUnit > 0
      );
    },

    /* =====================================================
       TRANSFER VALID
       ===================================================== */

    transferValid() {
      return Boolean(
        this.transferDate &&
          this.transferReference &&
          this.transferReference.length === 8 &&
          this.fromVenueId &&
          this.toVenueId &&
          this.fromVenueId !== this.toVenueId &&
          this.lines.length &&
          this.lines.every((line) => {
            return (
              this.number(line.quantity_order_units) > 0 &&
              this.number(line.quantity_transferred) > 0 &&
              this.number(line.stock_units_per_order_unit) > 0
            );
          })
      );
    },

    /* =====================================================
       TRANSFER TOTAL
       ===================================================== */

    transferTotal() {
      return this.lines.reduce((total, line) => total + this.lineTotal(line), 0);
    },
  },

  watch: {
    transferDate() {
      this.generateTransferReference();
    },
  },

  mounted() {
    this.initModal();
  },

  beforeUnmount() {
    this.modalInstance?.destroy?.();

    this.modalInstance = null;
  },

  methods: {
    /* =====================================================
       NUMBER
       ===================================================== */

    number(value) {
      const number = Number(value);

      return Number.isFinite(number) ? number : 0;
    },

    /* =====================================================
       MODAL
       ===================================================== */

    initModal() {
      if (!window.M || !this.$refs.modalElement) {
        return;
      }

      this.modalInstance = window.M.Modal.init(this.$refs.modalElement, {
        dismissible: true,

        opacity: 0.5,

        inDuration: 150,

        outDuration: 120,

        onCloseEnd: () => {
          this.resetForm();
        },
      });
    },

    /* =====================================================
       OPEN
       ===================================================== */

    async open() {
      this.resetForm();

      this.generateTransferReference();

      this.loading = true;

      this.modalInstance?.open?.();

      try {
        const [stockItems, venues] = await Promise.all([
          fetchStockUnits(),

          listVenuesForSnapshotDropdown({
            isOperations: !!this.contextIsOperations,

            userType: this.contextUserType,

            allowedVenueIds: Array.isArray(this.contextAllowedVenueIds)
              ? this.contextAllowedVenueIds
              : [],
          }),
        ]);

        this.stockItems = Array.isArray(stockItems) ? stockItems : [];

        this.venues = Array.isArray(venues) ? venues : [];

        if (!this.stockItems.length) {
          throw new Error("There are no products in the Stock Database.");
        }

        if (
          this.activeVenueId &&
          this.venues.some((venue) => venue.id === this.activeVenueId)
        ) {
          this.fromVenueId = this.activeVenueId;
        }
      } catch (error) {
        console.error("[INTERNAL TRANSFER] Load failed:", error);

        this.error = error?.message || "Unable to prepare internal transfer.";
      } finally {
        this.loading = false;
      }
    },

    /* =====================================================
       CLOSE
       ===================================================== */

    close() {
      this.modalInstance?.close?.();
    },

    /* =====================================================
       USER INITIALS
       ===================================================== */

    getUserInitials() {
      const user = auth.currentUser;

      const displayName = String(user?.displayName ?? "").trim();

      if (displayName) {
        const parts = displayName
          .split(/\s+/)
          .map((part) => part.trim())
          .filter(Boolean);

        if (parts.length >= 2) {
          return ((parts[0]?.[0] || "") + (parts[parts.length - 1]?.[0] || ""))
            .toUpperCase()
            .slice(0, 2);
        }

        if (parts.length === 1) {
          return parts[0]
            .replace(/[^a-z0-9]/gi, "")
            .slice(0, 2)
            .toUpperCase()
            .padEnd(2, "X");
        }
      }

      const email = String(user?.email ?? "").trim();

      if (email) {
        const localPart = email.split("@")[0].trim();

        const parts = localPart
          .split(/[._\-\s]+/)
          .map((part) => part.trim())
          .filter(Boolean);

        if (parts.length >= 2) {
          return ((parts[0]?.[0] || "") + (parts[parts.length - 1]?.[0] || ""))
            .toUpperCase()
            .slice(0, 2);
        }

        if (parts.length === 1) {
          return parts[0]
            .replace(/[^a-z0-9]/gi, "")
            .slice(0, 2)
            .toUpperCase()
            .padEnd(2, "X");
        }
      }

      return "XX";
    },

    /* =====================================================
       TRANSFER REFERENCE
       ===================================================== */

    generateTransferReference() {
      const date = String(this.transferDate ?? "").trim();

      if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        this.transferReference = "";

        return;
      }

      const [year, month, day] = date.split("-");

      const shortYear = year.slice(-2);

      const initials = this.getUserInitials()
        .replace(/[^A-Z0-9]/gi, "")
        .toUpperCase()
        .slice(0, 2)
        .padEnd(2, "X");

      this.transferReference = `${day}${month}${shortYear}${initials}`;
    },

    /* =====================================================
       VENUE LABEL
       ===================================================== */

    venueLabel(venueId) {
      return this.venues.find((venue) => venue.id === venueId)?.label || venueId || "—";
    },

    /* =====================================================
       FROM VENUE CHANGED
       ===================================================== */

    fromVenueChanged() {
      if (this.toVenueId === this.fromVenueId) {
        this.toVenueId = "";
      }
    },

    /* =====================================================
       PRODUCT CHANGED
       ===================================================== */

    productChanged() {
      this.newLine.product_id = "";

      this.newLine.quantity = 0;
    },

    /* =====================================================
       SUPPLIER CHANGED
       ===================================================== */

    supplierChanged() {
      this.newLine.quantity = 0;
    },

    /* =====================================================
       ORDER UNIT

       Same fallback structure used by Delivery Note.
       ===================================================== */

    orderUnitFor(product) {
      if (!product) {
        return "";
      }

      return String(
        product.order_unit || product.orderUnit || product.unit_size || ""
      ).trim();
    },

    /* =====================================================
       STOCK UNIT
       ===================================================== */

    stockUnitFor(product) {
      if (!product) {
        return "";
      }

      return String(
        product.stock_unit || product.stockUnit || product.unit_size || ""
      ).trim();
    },

    /* =====================================================
       STOCK UNITS PER ORDER UNIT

       Preferred:
         stock_units_per_order_unit

       Compatibility fallbacks:
         stockUnitsPerOrderUnit
         stock_units
         stockUnits
       ===================================================== */

    stockUnitsPerOrderUnit(product) {
      if (!product) {
        return 0;
      }

      return (
        this.number(product.stock_units_per_order_unit) ||
        this.number(product.stockUnitsPerOrderUnit) ||
        this.number(product.stock_units) ||
        this.number(product.stockUnits)
      );
    },

    /* =====================================================
       ORDER UNIT COST
       ===================================================== */

    orderUnitCost(product) {
      if (!product) {
        return 0;
      }

      return this.number(product.unit_cost ?? product.unitCost ?? product.unitPrice);
    },

    /* =====================================================
       STOCK UNIT COST

       The existing transfer API calculates:

         quantity_transferred × unit_cost

       quantity_transferred is now STOCK UNITS.

       Therefore we pass the proportional cost of one
       Stock Unit into the existing API.

       Example:
         Case cost = £24
         Case contains 24 bottles

         Stock Unit Cost = £24 / 24
                         = £1 per bottle

         48 bottles × £1 = £48 transfer value
       ===================================================== */

    stockUnitCost(product) {
      const orderCost = this.orderUnitCost(product);

      const conversion = this.stockUnitsPerOrderUnit(product);

      if (conversion <= 0) {
        return 0;
      }

      return orderCost / conversion;
    },

    /* =====================================================
       ADD LINE
       ===================================================== */

    addLine() {
      const variant = this.selectedVariant;

      if (!variant) {
        return;
      }

      const quantityOrderUnits = this.number(this.newLine.quantity);

      const conversion = this.stockUnitsPerOrderUnit(variant);

      if (quantityOrderUnits <= 0 || conversion <= 0) {
        return;
      }

      const name = String(variant.item_name ?? "").trim();

      const group = String(variant.product_group ?? "").trim();

      const key = productKey(group, name);

      if (this.lines.some((line) => line.product_key === key)) {
        return;
      }

      const orderUnit = this.orderUnitFor(variant);

      const stockUnit = this.stockUnitFor(variant);

      const quantityStockUnits = quantityOrderUnits * conversion;

      const orderCost = this.orderUnitCost(variant);

      const stockCost = this.stockUnitCost(variant);

      this.lines.push({
        /* -----------------------------------------------
           PRODUCT IDENTITY
           ----------------------------------------------- */

        product_key: key,

        product_id: variant.id,

        product_name: name,

        product_group: group,

        supplier: String(variant.supplier ?? "").trim(),

        /* -----------------------------------------------
           UNIT SNAPSHOT
           ----------------------------------------------- */

        order_unit: orderUnit,

        stock_unit: stockUnit,

        stock_units_per_order_unit: conversion,

        /* -----------------------------------------------
           LEGACY / COUNT COMPATIBILITY
           ----------------------------------------------- */

        unit_size: stockUnit || String(variant.unit_size ?? "").trim(),

        stock_units_per_unit: this.number(variant.stock_units ?? variant.stockUnits),

        /* -----------------------------------------------
           COSTS

           unit_cost is intentionally STOCK UNIT COST
           because the current transfer API multiplies it
           by quantity_transferred.
           ----------------------------------------------- */

        order_unit_cost: orderCost,

        unit_cost: stockCost,

        /* -----------------------------------------------
           QUANTITIES

           quantity_order_units = what the user entered.

           quantity_transferred = converted Stock Units.

           IMPORTANT:
           Existing StockCount logic reads
           quantity_transferred directly into Stock In/Out.
           ----------------------------------------------- */

        quantity_order_units: quantityOrderUnits,

        quantity_transferred: quantityStockUnits,
      });

      this.newLine = emptyLine();
    },

    /* =====================================================
       REMOVE LINE
       ===================================================== */

    removeLine(productKeyValue) {
      this.lines = this.lines.filter((line) => line.product_key !== productKeyValue);
    },

    /* =====================================================
       LINE TOTAL

       Value is based on Order Units × Order Unit Cost.
       ===================================================== */

    lineTotal(line) {
      return this.number(line.quantity_order_units) * this.number(line.order_unit_cost);
    },

    /* =====================================================
       SAVE TRANSFER
       ===================================================== */

    async saveTransfer() {
      if (!this.transferValid || this.saving) {
        return;
      }

      this.saving = true;

      this.error = null;

      try {
        const result = await saveStockInternalTransfer({
          transferDate: this.transferDate,

          transferReference: this.transferReference,

          fromVenueId: this.fromVenueId,

          fromVenueName: this.venueLabel(this.fromVenueId),

          toVenueId: this.toVenueId,

          toVenueName: this.venueLabel(this.toVenueId),

          /*
           * IMPORTANT:
           *
           * quantity_transferred is already converted
           * into STOCK UNITS before reaching the API.
           *
           * Therefore the existing API will store the
           * correct stock movement for both venues.
           */
          lines: this.lines,
        });

        this.$emit("saved", result);

        window.M?.toast?.({
          html: "Internal transfer saved successfully.",

          classes: "green darken-2",
        });

        this.close();
      } catch (error) {
        console.error("[INTERNAL TRANSFER] Save failed:", error);

        this.error =
          error?.details?.message ||
          error?.message ||
          "Unable to save internal transfer.";
      } finally {
        this.saving = false;
      }
    },

    /* =====================================================
       RESET
       ===================================================== */

    resetForm() {
      this.stockItems = [];

      this.venues = [];

      this.transferDate = today();

      this.transferReference = "";

      this.fromVenueId = "";

      this.toVenueId = "";

      this.lines = [];

      this.newLine = emptyLine();

      this.loading = false;

      this.saving = false;

      this.error = null;

      this.generateTransferReference();
    },

    /* =====================================================
       MONEY
       ===================================================== */

    money(value) {
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(this.number(value));
    },

    /* =====================================================
       NUMBER FORMAT
       ===================================================== */

    formatNumber(value) {
      return this.number(value).toLocaleString("en-GB", {
        maximumFractionDigits: 4,
      });
    },
  },
};
</script>

<style scoped>
.modal-heading-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.modal-heading {
  margin: 0;
}

.modal-subtitle {
  max-width: 760px;
  margin: 5px 0 0;
}

.modal-x-button {
  flex: 0 0 auto;
}

.error-box {
  margin: 16px 0;
}

.transfer-header-grid {
  display: grid;
  grid-template-columns:
    180px
    minmax(180px, 1fr)
    minmax(180px, 1fr)
    minmax(180px, 1fr);
  gap: 14px;
  margin-top: 22px;
}

.form-field label {
  display: block;
  position: static;
  margin-bottom: 6px;
  color: #607d8b;
  font-size: 0.8rem;
  font-weight: 600;
  transform: none;
}

.form-input {
  box-sizing: border-box !important;
  width: 100% !important;
  height: 40px !important;
  margin: 0 !important;
  padding: 0 8px !important;
  border: 1px solid #bdbdbd !important;
  border-radius: 4px !important;
  box-shadow: none !important;
}

.form-input:focus {
  border-color: #00897b !important;
  box-shadow: 0 0 0 1px #00897b !important;
  outline: none;
}

.form-input:disabled {
  background: #f5f5f5;
  color: #9e9e9e;
}

.generated-reference {
  background: #f5f5f5 !important;
  color: #37474f;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.field-helper {
  display: block;
  margin-top: 4px;
  color: #9e9e9e;
  font-size: 0.7rem;
}

.form-select {
  box-sizing: border-box;
  width: 100%;
  height: 40px;
  padding: 0 7px;
  border: 1px solid #bdbdbd;
  border-radius: 4px;
  background: #ffffff;
}

.form-select:focus {
  border-color: #00897b;
  outline: none;
  box-shadow: 0 0 0 1px #00897b;
}

.form-select:disabled {
  background: #f5f5f5;
  color: #9e9e9e;
}

.unit-display {
  display: flex;
  box-sizing: border-box;
  align-items: center;
  width: 100%;
  height: 40px;
  padding: 0 10px;
  border: 1px solid #d7e7e4;
  border-radius: 4px;
  background: #f5fbfa;
  color: #00695c;
  font-weight: 700;
}

.unit-display--empty {
  border-color: #e0e0e0;
  background: #f5f5f5;
  color: #9e9e9e;
}

.movement-summary {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  margin-top: 18px;
  padding: 12px;
  border-radius: 6px;
  background: #eceff1;
}

.movement-side {
  display: flex;
  align-items: center;
  gap: 8px;
}

.movement-side div {
  display: flex;
  flex-direction: column;
}

.movement-out {
  color: #c62828;
}

.movement-in {
  color: #2e7d32;
}

.movement-arrow {
  color: #607d8b;
}

.add-product-panel {
  margin-top: 20px;
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 7px;
  background: #fafafa;
}

.add-product-grid {
  display: grid;
  grid-template-columns:
    minmax(180px, 2fr)
    minmax(160px, 1.4fr)
    minmax(120px, 0.8fr)
    140px
    auto;
  align-items: end;
  gap: 12px;
}

.add-product-action {
  display: flex;
  align-items: flex-end;
}

.conversion-preview {
  display: flex;
  align-items: stretch;
  gap: 12px;
  margin-top: 14px;
}

.conversion-definition,
.conversion-result {
  flex: 1;
  padding: 10px 12px;
  border-radius: 7px;
}

.conversion-definition {
  border: 1px solid #d7e7e4;
  background: #f5fbfa;
}

.conversion-result {
  border: 1px solid #b2dfdb;
  background: #e0f2f1;
}

.conversion-definition span,
.conversion-definition strong,
.conversion-result span,
.conversion-result strong {
  display: block;
}

.conversion-definition span,
.conversion-result span {
  margin-bottom: 3px;
  color: #607d8b;
  font-size: 0.74rem;
}

.conversion-definition strong,
.conversion-result strong {
  color: #00695c;
}

.conversion-warning {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 7px;
  background: #fff8e1;
  color: #e65100;
  font-size: 0.82rem;
}

.conversion-warning i {
  flex: 0 0 auto;
  font-size: 20px;
}

.transfer-lines-wrap {
  margin-top: 20px;
}

.transfer-lines-table {
  min-width: 900px;
}

.product-name {
  min-width: 190px;
  font-weight: 600;
}

.stock-movement-cell {
  min-width: 145px;
  color: #00695c;
}

.line-total {
  min-width: 100px;
  font-weight: 700;
}

.remove-button {
  padding: 0 5px;
}

.transfer-explanation {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
  padding: 10px 12px;
  border-radius: 7px;
  background: #eceff1;
  color: #455a64;
  font-size: 0.8rem;
}

.transfer-explanation i {
  flex: 0 0 auto;
  color: #607d8b;
  font-size: 20px;
}

.transfer-total {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 30px;
  margin-top: 12px;
  padding: 12px 10px;
  border-top: 1px solid #e0e0e0;
  font-size: 1rem;
}

.transfer-total strong {
  min-width: 110px;
  text-align: right;
  font-size: 1.1rem;
}

.empty-lines {
  margin-top: 20px;
}

@media only screen and (max-width: 1000px) {
  .transfer-header-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .add-product-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .add-product-action {
    align-self: end;
  }
}

@media only screen and (max-width: 700px) {
  .transfer-header-grid,
  .add-product-grid {
    grid-template-columns: 1fr;
  }

  .conversion-preview {
    flex-direction: column;
  }

  .movement-summary {
    gap: 10px;
    font-size: 0.8rem;
  }
}
</style>
